import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSubscription } from 'react-stomp-hooks';
import { NavLink as RouterLink } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

// @mui
import { Box, List, Badge, Tooltip, ListItemText } from '@mui/material';

import { usePermission } from 'src/hooks/usePermission';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { fetchPendingAccessRequests } from 'src/services/department.service';

//
import { StyledNavItem, StyledNavItemIcon } from './styles';

// ----------------------------------------------------------------------

NavSection.propTypes = {
  data: PropTypes.array,
  isExpanded: PropTypes.bool,
};

export default function NavSection({ data = [], isExpanded = true, ...other }) {
  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {data.map((item) => (
          <NavItem key={item.title} item={item} isExpanded={isExpanded} />
        ))}
      </List>
    </Box>
  );
}

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  isExpanded: PropTypes.bool,
};

function NavItem({ item, isExpanded }) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { title, path, icon, info } = item;

  const [pendingCount, setPendingCount] = useState(0);
  const { can } = usePermission();

  const hasUpdatePermission = can({ permission: 'DEPARTMENT:UPDATE' });
  const isTicketsItem = item.title?.toLowerCase() === 'tickets';

  // Fetch the count of pending access requests for the notification dot
  const fetchCount = useCallback(() => {
    if (isTicketsItem && hasUpdatePermission) {
      fetchPendingAccessRequests()
        .then((res) => {
          setPendingCount((res || []).length);
        })
        .catch((err) =>
          console.error('Failed to load pending requests count inside sidebar:', err)
        );
    }
  }, [isTicketsItem, hasUpdatePermission]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Real-Time Listener: Instantly updates the sidebar dot count when requests are processed or added
  useSubscription('/queue/access-requests', () => {
    fetchCount();
  });

  return (
    <Tooltip title={!isExpanded ? t(`nav.${title}`) : ''} placement="right" arrow>
      <span>
        <StyledNavItem
          component={RouterLink}
          to={path}
          sx={{
            '&.active': {
              color: 'text.primary',
              bgcolor: 'action.selected',
              fontWeight: 'fontWeightBold',
            },
            justifyContent: 'flex-start',
            px: isExpanded ? 2.5 : 0,
            height: isSmallScreen ? 40 : 44,
            minHeight: isSmallScreen ? 40 : 44,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'padding, height',
          }}
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!(isTicketsItem && pendingCount > 0)} // Only show on Tickets link if count > 0
            sx={{
              '& .MuiBadge-badge': {
                right: isExpanded ? 4 : -2, // Adapts position based on sidebar collapse state
                top: isExpanded ? 4 : -2,
                boxShadow: '0 0 0 0 rgba(255, 72, 66, 0.7)',
                animation:
                  isTicketsItem && pendingCount > 0
                    ? 'pulse-sidebar 2s infinite'
                    : 'none',
                '@keyframes pulse-sidebar': {
                  '0%': {
                    transform: 'scale(0.95)',
                    boxShadow: '0 0 0 0 rgba(255, 72, 66, 0.7)',
                  },
                  '70%': {
                    transform: 'scale(1)',
                    boxShadow: '0 0 0 4px rgba(255, 72, 66, 0)',
                  },
                  '100%': {
                    transform: 'scale(0.95)',
                    boxShadow: '0 0 0 0 rgba(255, 72, 66, 0)',
                  },
                },
              },
            }}
          >
            <StyledNavItemIcon
              sx={{
                width: isSmallScreen ? 20 : 22,
                height: isSmallScreen ? 20 : 22,
                m: isExpanded ? '0 16px 0 0' : '7px',
                minWidth: isExpanded ? '40px' : 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'margin 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'margin',
              }}
            >
              {icon && icon}
            </StyledNavItemIcon>
          </Badge>

          <ListItemText
            disableTypography
            primary={t(`nav.${title}`)}
            sx={{
              fontSize: isSmallScreen ? '0.725rem' : '0.875rem',
              opacity: isExpanded ? 1 : 0,
              width: isExpanded ? '160px' : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
              transition:
                'opacity 0.15s ease, width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s ease',
              willChange: 'opacity, width, transform',
            }}
          />

          {isExpanded && info && info}
        </StyledNavItem>
      </span>
    </Tooltip>
  );
}
